/**
 * Integrations › Meta (wave 7b): the tenant's connection to Meta and which ad
 * accounts feed which website. `/api/v1/integrations/meta`, Owner and
 * Administrator only (`integrations.manage`).
 *
 * The token never reaches the browser. Connecting is a full-page round trip:
 * `connectMeta()` answers Facebook's Login URL, the browser goes there in the
 * same tab, and Meta sends it back to `/integrations/meta?connected=1` — or
 * `?error=<code>`, which `META_CALLBACK_ERRORS` turns into an instruction.
 */

import type {
  Environment,
  MetaAdAccountList,
  MetaAdAccountMapping,
  MetaImportRun,
  MetaIntegrationState,
  MetaLastImports,
  MetaMappedAdAccount,
  MetaMappingResult,
} from './types'

export interface IntegrationsApi {
  metaIntegration(): Promise<MetaIntegrationState>
  /** Where to send the browser to start connecting. */
  connectMeta(): Promise<MetaConnectStart>
  disconnectMeta(): Promise<MetaIntegrationState>
  metaAdAccounts(): Promise<MetaAdAccountList>
  /** Replaces the whole mapping: accounts left out stop feeding any website. */
  saveMetaAdAccounts(mappings: readonly MetaAdAccountMapping[]): Promise<MetaMappingResult>
  /**
   * "Run import now": the hierarchy, then today's spend, for this tenant's mapped ad
   * accounts. Rate-limited per tenant (429 with `Retry-After`); a provider refusal
   * comes back as a failed run in `lastImports`, not as a failed call.
   */
  runMetaImportNow(): Promise<MetaIntegrationState>
}

/**
 * What the callback's `?error=` codes mean to the person who pressed Connect.
 * The server's list is the `CallbackError` enum in `modules/advertising/meta.py`; a test
 * there fails if a code has no entry here.
 */
export const META_CALLBACK_ERRORS: Record<string, string> = {
  declined: 'Facebook was closed before the connection was approved. Press Connect Meta to try again.',
  missing_permission:
    'Competition Insights needs permission to read your ads ("ads_read"). Press Connect Meta again and keep that permission ticked when Facebook asks.',
  invalid_state:
    'That connection attempt expired or was started by someone else. Press Connect Meta to start again — it has to be finished within ten minutes by the person who started it.',
  not_configured: 'Meta is not configured on this environment. Contact TmT support.',
  meta_unavailable: 'Meta did not answer just now. Wait a few minutes and press Connect Meta again.',
  meta_refused: 'Meta refused the connection. Press Connect Meta to start again.',
  app_misconfigured:
    'The Competition Insights Meta app is not set up for this address yet. Contact TmT support; nothing on your side needs to change.',
  conflict: 'Someone else connected Meta at the same moment, and their connection was kept. Reload the page to see it.',
}

export interface MetaConnectStart {
  /** Facebook's Login dialog, or — when `completesInApp` — a path inside this app. */
  authorizeUrl: string
  /**
   * True only for the demo client, which "connects" at once and lands where the
   * callback would, without leaving the app. The real flow always leaves for
   * Facebook in this tab.
   */
  completesInApp: boolean
}

// --- wire shapes (snake_case, as the API sends them) -------------------------

export interface MappedAdAccountOut {
  ad_account_id: string
  external_ad_account_id: string
  name: string | null
  currency: string
  timezone: string | null
  website_id: string
  environment: Environment
  status: string | null
}

export interface ImportRunOut {
  started_at: string
  finished_at: string | null
  status: MetaImportRun['status']
  rows_written: number
  error: string | null
}

export interface LastImportsOut {
  hierarchy: ImportRunOut | null
  insights: ImportRunOut | null
}

export interface MetaIntegrationOut {
  configured: boolean
  missing_configuration: string[]
  status: MetaIntegrationState['status']
  connected_user_name: string | null
  scopes: string[]
  token_expires_at: string | null
  connected_at: string | null
  last_checked_at: string | null
  last_error: string | null
  tenant_currency: string
  refresh_window_days: number
  ad_accounts: MappedAdAccountOut[]
  last_imports: LastImportsOut
  provider_lag: boolean
}

export interface AdAccountListOut {
  tenant_currency: string
  items: Array<{
    external_ad_account_id: string
    account_id: string
    name: string | null
    currency: string
    timezone_name: string | null
    account_status: string | null
    website_id: string | null
    currency_matches_tenant: boolean
  }>
}

export interface AdAccountMappingOut {
  items: MappedAdAccountOut[]
  unmapped: string[]
  warnings: Array<{ external_ad_account_id: string; code: 'currency_mismatch'; message: string }>
}

export function mappedFromWire(row: MappedAdAccountOut): MetaMappedAdAccount {
  return {
    adAccountId: row.ad_account_id,
    externalAdAccountId: row.external_ad_account_id,
    name: row.name,
    currency: row.currency,
    timezone: row.timezone,
    websiteId: row.website_id,
    environment: row.environment,
    status: row.status,
  }
}

export function importRunFromWire(run: ImportRunOut | null): MetaImportRun | null {
  if (!run) return null
  return {
    startedAt: run.started_at,
    finishedAt: run.finished_at,
    status: run.status,
    rowsWritten: run.rows_written,
    error: run.error,
  }
}

export function lastImportsFromWire(body: LastImportsOut | undefined): MetaLastImports {
  return {
    hierarchy: importRunFromWire(body?.hierarchy ?? null),
    insights: importRunFromWire(body?.insights ?? null),
  }
}

export function metaStateFromWire(body: MetaIntegrationOut): MetaIntegrationState {
  return {
    configured: body.configured,
    missingConfiguration: body.missing_configuration,
    status: body.status,
    connectedUserName: body.connected_user_name,
    scopes: body.scopes,
    tokenExpiresAt: body.token_expires_at,
    connectedAt: body.connected_at,
    lastCheckedAt: body.last_checked_at,
    lastError: body.last_error,
    tenantCurrency: body.tenant_currency,
    refreshWindowDays: body.refresh_window_days,
    adAccounts: body.ad_accounts.map(mappedFromWire),
    lastImports: lastImportsFromWire(body.last_imports),
    providerLag: body.provider_lag,
  }
}

export function adAccountsFromWire(body: AdAccountListOut): MetaAdAccountList {
  return {
    tenantCurrency: body.tenant_currency,
    items: body.items.map((item) => ({
      externalAdAccountId: item.external_ad_account_id,
      accountId: item.account_id,
      name: item.name,
      currency: item.currency,
      timezoneName: item.timezone_name,
      accountStatus: item.account_status,
      websiteId: item.website_id,
      currencyMatchesTenant: item.currency_matches_tenant,
    })),
  }
}

export function mappingFromWire(body: AdAccountMappingOut): MetaMappingResult {
  return {
    items: body.items.map(mappedFromWire),
    unmapped: body.unmapped,
    warnings: body.warnings.map((warning) => ({
      externalAdAccountId: warning.external_ad_account_id,
      code: warning.code,
      message: warning.message,
    })),
  }
}

export function mappingToWire(mappings: readonly MetaAdAccountMapping[]) {
  return mappings.map((mapping) => ({
    external_ad_account_id: mapping.externalAdAccountId,
    website_id: mapping.websiteId,
  }))
}
